/**
 * Domain formatting for the back office.
 *
 * These are the answers that are the same in every language once you hand them a
 * translator: which words describe a term, what "valid until" means for a
 * subscription that has no end date, how close a lapse is. Everything that is
 * genuinely locale work — digits, dates, money, plurals — belongs to
 * `useTranslation()` and is NOT re-implemented here.
 *
 * That split is why the previous version of this file is gone rather than
 * extended. It exported `toFa`, `faAmount`, `jalaliDate` and `rialToToman`, so
 * every render site called a Persian-only formatter directly: the panel rendered
 * «۱۲ مرداد ۱۴۰۴» and «۱۵٬۰۰۰٬۰۰۰ تومان» whatever language it was set to, and a
 * render site knew which language was active — which DESIGN-CONCEPT §16.1 names
 * as the defect, because a single seam is the only way the rule can hold.
 *
 * Every function takes `t` (and a formatter where it needs one) as an argument
 * rather than calling a hook, so they stay pure and testable without rendering.
 */

import {
  ORIGIN_KEY, PERIOD_SOURCE_KEY, EVENT_TYPE_KEY, tierLabelKey,
} from '../constants/subscription';
import { LAPSE_SOON_DAYS } from '../constants/list';
import {
  CURRENCIES, DEFAULT_CURRENCY, currencyInfo, isClinicCurrency, unitLabel,
} from '@parsina/framework/ui/lib/currency';
import { fallbackLocalesFor } from '../i18n/config';

/** What every "no value" renders as. Reads as absence in both scripts. */
export const EMPTY = '—';

/**
 * What plan a clinic is on, in words.
 *
 * Answers with the TIER, not the plan code, because the tier is the fact an
 * operator needs: a trial and a grandfathered clinic have no plan code at all
 * and are both on طلایی. The predecessor was a ternary that knew exactly two
 * codes — `CLINIC_ANNUAL` and `CLINIC_MONTHLY`, both since retired — so every
 * clinic on the tiered catalogue rendered as «—», i.e. "no plan", right after
 * paying. It was copy-pasted into two screens, which is why the bug shipped
 * twice; there is one function now and no ternary.
 *
 * Resolution order, and each step exists for a reason:
 *   1. the catalogue, keyed on `capabilitySet` — translatable, so an English
 *      panel says "Gold" rather than «طلایی»
 *   2. the raw `capabilitySet` — a tier this build has never been taught is
 *      echoed AS ITSELF, never as a dash and never as a plausible wrong label
 *   3. the server's `tierNameFa` — Persian-only, so it is a fallback rather than
 *      the preference it used to be
 *   4. `planCode`, then EMPTY
 */
export function planLabel({ planCode, capabilitySet, tierNameFa } = {}, t) {
  const key = tierLabelKey(capabilitySet);
  if (key) return t(key);
  if (capabilitySet) return capabilitySet;
  if (tierNameFa) return tierNameFa;
  return planCode || EMPTY;
}

/**
 * How long a term runs, from the plan's own `periodMonths`.
 *
 * 0 means it NEVER ENDS — true of the free پایه floor and of دائمی — which is a
 * term, not a missing value.
 */
export function termLabel(periodMonths, t) {
  if (!periodMonths) return t('common.term.unlimited');
  if (periodMonths === 12) return t('common.term.annual');
  if (periodMonths === 1) return t('common.term.monthly');
  return t('common.term.months', { count: periodMonths });
}

/**
 * How long a clinic's access is good for — ONE answer, used by the list column
 * and by the record's field, which previously disagreed.
 *
 * The list said `currentPeriodEnd || trialEndsAt || '—'` and the record said
 * `currentPeriodEnd || 'نامحدود'`, so the same clinic read differently on the two
 * screens an operator moves between while working one ticket.
 *
 * "No end date" means two genuinely different things and the plan code is what
 * tells them apart: a دائمی PURCHASE (they bought a term that never ends) versus
 * a clinic backfilled at go-live and never billed. Collapsing them loses the
 * distinction between the largest single payment the platform takes and a clinic
 * that has never paid a rial.
 */
export function validUntil(subscription, { t, formatDate }) {
  if (!subscription) return EMPTY;
  if (subscription.currentPeriodEnd) return formatDate(subscription.currentPeriodEnd);
  if (subscription.trialEndsAt) {
    return t('clinic.unlimited.trial', { date: formatDate(subscription.trialEndsAt) });
  }
  return subscription.planCode
    ? t('clinic.unlimited.perpetual')
    : t('clinic.unlimited.grandfathered');
}

/**
 * Whole days from now until `iso` — negative once it is past, null when there is
 * no date to count to.
 *
 * Counted on the CALENDAR DAY, not on elapsed hours: an operator asking "when
 * does this lapse" means dates, so a term ending in nine hours is «today» and
 * not «0 days». `now` is injectable so a test can pin the clock.
 */
export function lapseDays(iso, now = Date.now()) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const day = 86400000;
  return Math.floor(end / day) - Math.floor(now / day);
}

/**
 * The short amber/red hint beside a date, or null when the date needs no comment.
 *
 * Returns `{ tone, text }` rather than a node so it is testable and so the
 * caller owns the markup. `tone` is 'warn' inside the lapse window, 'danger'
 * once past, and the hint is absent for a term with months left — a list where
 * every row carries a hint has no hints.
 */
export function lapseHint(iso, { t, now = Date.now() } = {}) {
  const days = lapseDays(iso, now);
  if (days === null) return null;
  if (days < 0) return { tone: 'danger', text: t('clinics.lapse.pastDays', { count: -days }) };
  if (days === 0) return { tone: 'danger', text: t('clinics.lapse.today') };
  if (days <= LAPSE_SOON_DAYS) return { tone: 'warn', text: t('clinics.lapse.inDays', { count: days }) };
  return null;
}

/** Which date a clinic's access actually hinges on, for the list column's hint. */
export const lapseAnchor = (row) => row?.currentPeriodEnd || row?.trialEndsAt || null;

/**
 * A subscription's origin, in words. An origin the panel has not been taught is
 * echoed rather than hidden, like an unknown tier.
 */
export function originLabel(origin, t) {
  const key = ORIGIN_KEY[origin];
  return key ? t(key) : (origin || EMPTY);
}

/** A billing period's kind, in words. */
export function periodSourceLabel(source, t) {
  const key = PERIOD_SOURCE_KEY[source];
  return key ? t(key) : (source || EMPTY);
}

/**
 * An event type, in words — or `null` when this build has never been taught it,
 * which tells the caller to render the raw type as an LTR code instead of
 * inventing a label for it.
 */
export function eventTypeLabel(eventType, t) {
  const key = EVENT_TYPE_KEY[eventType];
  return key ? t(key) : null;
}

// NOTE — there is deliberately no `rialToToman` here any more.
//
// `formatMoney(rials)` from `useTranslation()` already takes WHOLE RIALS and
// renders toman in the reader's digits, so a second division in this file would
// be a second place the ÷10 could be got wrong or forgotten. A cell that wants
// the bare figure passes `withUnit: false` (the default) and lets its column
// header carry «تومان»; a sentence that needs the word passes `withUnit: true`.
// One conversion, one seam.

/**
 * A USD figure, to four decimals, in the reader's digits.
 *
 * A dollar amount is a NUMBER, not an identifier, so it follows the locale's
 * numbering system like every other quantity — it is deliberately not an LTR
 * island. Four decimals because a single LLM call costs fractions of a cent
 * ($0.00042) and «۰٫۰۰» would report a cost that exists as nothing.
 *
 * Below the smallest displayable figure the answer is «< ۰٫۰۰۰۱» rather than a
 * rounded zero: the ledger stores eight decimals, and a call that cost
 * $0.00000030 did cost something. `null`/`undefined`/'' render as the dash —
 * the AI service's NULL means "unpriced", never "free" (design §7.1).
 */
export const USD_DECIMALS = 4;
const USD_FLOOR = 10 ** -USD_DECIMALS;

export function formatUsd(value, { formatNumber, t }) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return EMPTY;
  const n = Number(value);
  const options = { minimumFractionDigits: 0, maximumFractionDigits: USD_DECIMALS };
  if (n > 0 && n < USD_FLOOR) {
    return t('aiPricing.usd.tiny', { threshold: formatNumber(USD_FLOOR, options) });
  }
  return formatNumber(n, options);
}

/**
 * The currency a clinic keeps its books in, as a word
 * (docs/clinic-currency/CONTRACTS.md §10).
 *
 * IRR — and a blank currency, which IRR is — reads this app's own catalogue
 * word, `common.units.tomanName` (the currency's NAME; `common.units.toman` is
 * the word after an amount, which French puts in the plural), because every IRR figure this panel shows is in
 * toman and that word already exists in each language. Any other allow-listed
 * code reads the kit's currency table in `locale`. A code outside the table
 * answers null: this is the ops panel, where a bad value must be SEEN as
 * itself rather than crash the page or pass for toman.
 */
export function currencyName(code, { t, locale }) {
  const c = code || DEFAULT_CURRENCY;
  if (c === DEFAULT_CURRENCY) return t('common.units.tomanName');
  return isClinicCurrency(c) ? unitLabel(c, kitLabelLocale(c, locale)) : null;
}

/**
 * The language to ask the kit's currency table in: `locale` when the table has
 * a label in it, else the nearest hop of the panel's own fallback chain that
 * it has (fr → en → fa, PM-0185 D4). A kit that does not know a language reads
 * it as Persian — the pinned v0.21.0 table has no French — and a French reader
 * must be shown English before Persian.
 */
function kitLabelLocale(code, locale) {
  const labels = CURRENCIES[code]?.labels || {};
  const known = (id) => Object.prototype.hasOwnProperty.call(labels, id);
  if (known(locale)) return locale;
  return fallbackLocalesFor(locale).find(known) || locale;
}

/** «درهم امارات (AED)» / "Toman (IRR)"; an unknown code is echoed as itself. */
export function currencyLabel(code, { t, locale }) {
  const c = code || DEFAULT_CURRENCY;
  const name = currencyName(c, { t, locale });
  return name ? t('clinic.fields.currencyValue', { name, code: c }) : c;
}

/** True for a clinic whose books are NOT in IRR — the exception an ops list marks. */
export const isForeignCurrency = (code) => !!code && code !== DEFAULT_CURRENCY;

// ── Platform money (PM-0211, docs/platform-money/CONTRACT.md) ───────────────
//
// What a clinic pays the platform is in the DEPLOYMENT's currency: IRR on
// Parsina — where every figure below reads exactly as it always did — and USD on
// Proxia. Each figure arrives beside its own `currency` (blank = IRR) and goes
// through `formatMoney(minor, { currency })`; only the unit WORD is decided
// here, so a header, a KPI and a sentence name it the same way.

/** True for IRR, and for a blank currency, which is IRR. */
export const isIrr = (code) => !code || code === DEFAULT_CURRENCY;

/**
 * The unit a money figure is read in: IRR's is this app's own «تومان» /
 * "Toman"; any other currency reads as its ISO code («USD»), which is the kit's
 * short label for every currency in its table and the honest echo of one that
 * is not.
 */
export function moneyUnit(code, { t, locale = 'fa' }) {
  if (isIrr(code)) return t('common.units.toman');
  return isClinicCurrency(code) ? unitLabel(code, locale, 'short') : code;
}

/**
 * The money price of ONE credit has a currency of its own. 'IRT' — and blank,
 * which is what a wallet or a policy from before PM-0211 sends — is today's
 * toman figure (`parsehToman`, converted with the policy's USD rate). Any other
 * currency is priced with no exchange rate at all.
 */
export const CREDIT_IRT = 'IRT';
export const isIrtCredit = (code) => !code || code === CREDIT_IRT;

/**
 * A credit price for a person: «۰٫۰۱ USD», or «۱٬۰۰۰ تومان» for IRT. `price` is
 * a DECIMAL in major units, as a pricing policy carries it; `{ minor: true }`
 * takes the wallet's integer minor units instead (IRT's minor unit is the
 * toman). A code outside the kit's table is echoed with the bare figure.
 */
export function formatCreditPrice(price, code, { t, formatNumber, formatMoney, minor = false }) {
  if (price == null || price === '' || !Number.isFinite(Number(price))) return EMPTY;
  if (isIrtCredit(code)) return `${formatNumber(Number(price))} ${t('common.units.toman')}`;
  if (!isClinicCurrency(code)) return `${price} ${code}`;
  const amountMinor = minor ? Number(price) : Math.round(Number(price) * currencyInfo(code).displayDivisor);
  return formatMoney(amountMinor, { currency: code, withUnit: true, unitStyle: 'short' });
}

/**
 * Whether a policy's credit price is no longer the wallet's: another currency,
 * or another amount in the same one. Unknown on either side is not a change.
 */
export function creditPriceDiffers({ price, currency }, wallet) {
  if (price == null || price === '' || !wallet?.amountMinor) return false;
  const policyCode = currency || CREDIT_IRT;
  const walletCode = wallet.currency || CREDIT_IRT;
  if (policyCode !== walletCode) return true;
  const divisor = isIrtCredit(walletCode) ? 1
    : isClinicCurrency(walletCode) ? currencyInfo(walletCode).displayDivisor : null;
  if (divisor == null) return false;
  return Math.round(Number(price) * divisor) !== Number(wallet.amountMinor);
}
